import axios from "axios";
import { useQuery } from "@tanstack/react-query";

const BASE = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({ baseURL: `${BASE}/api` });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("fosche_admin_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export interface Service {
  id: string;
  name: string;
  slug: string;
  tagline: string;
  description: string;
  features: string[];
  image: string;
  icon: string;
  order: number;
}

export interface Project {
  id: string;
  title: string;
  category: string;
  location: string;
  year: string;
  description: string;
  image: string;
  featured: boolean;
}

export interface Career {
  id: string;
  title: string;
  type: string;
  location: string;
  department: string;
  description: string;
  requirements: string[];
  is_open: boolean;
}

export interface Testimonial {
  id: string;
  quote: string;
  author: string;
  role: string;
  org: string;
}

export interface SiteSettings {
  company_name: string;
  tagline: string;
  address: string;
  phone: string;
  email: string;
  hours: string;
  uei: string;
  cage: string;
  certifications: string[];
  naics: string[];
  founder_name: string;
  founder_title: string;
}

export const useServices = () =>
  useQuery<Service[]>({ queryKey: ["services"], queryFn: async () => (await api.get("/content/services")).data });

export const useProjects = () =>
  useQuery<Project[]>({ queryKey: ["projects"], queryFn: async () => (await api.get("/content/projects")).data });

export const useCareers = () =>
  useQuery<Career[]>({ queryKey: ["careers"], queryFn: async () => (await api.get("/content/careers")).data });

export const useTestimonials = () =>
  useQuery<Testimonial[]>({ queryKey: ["testimonials"], queryFn: async () => (await api.get("/content/testimonials")).data });

export const useSettings = () =>
  useQuery<SiteSettings>({ queryKey: ["settings"], queryFn: async () => (await api.get("/content/settings")).data });

export function formatApiErrorDetail(detail: unknown): string {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  return String(detail);
}
